import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.jsx';
import { Button } from '../ui/button.jsx';
import { Input } from '../ui/input.jsx';
import { Badge } from '../ui/badge.jsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog.jsx';
import { Label } from '../ui/label.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.jsx';
import { Textarea } from '../ui/textarea.jsx';
import { useAuth } from '../../contexts/AuthContext';
// Using alert instead of toast for compatibility
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Award,
  Calendar,
  Mail,
  Globe,
  MoreHorizontal
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [affiliates, setAffiliates] = useState([]);
  const [filteredAffiliates, setFilteredAffiliates] = useState([]);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');

  // Edit form state
  const [editForm, setEditForm] = useState({
    status: '',
    tier: '',
    commission_rate_override: '',
    notes: ''
  });

  useEffect(() => {
    fetchAffiliates();
  }, []);

  useEffect(() => {
    filterAffiliates();
  }, [affiliates, searchTerm, statusFilter, tierFilter]);

  const fetchAffiliates = async () => {
    try {
      setLoading(true);
      const token = Cookies.get('enakid_token');
      
      if (!token) {
        alert('Authentication required. Please log in as admin.');
        return;
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/affiliates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data && response.data.affiliates) {
        setAffiliates(response.data.affiliates);
        console.log('Loaded affiliates:', response.data.affiliates.length);
      } else {
        setAffiliates([]);
      }
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      alert(`Failed to load affiliates: ${error.response?.data?.detail || error.message}`);
      setAffiliates([]);
    } finally {
      setLoading(false);
    }
  };

  const filterAffiliates = () => {
    let filtered = [...affiliates];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(affiliate => 
        affiliate.affiliate_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        affiliate.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        affiliate.user?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        affiliate.business_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(affiliate => affiliate.status === statusFilter);
    }

    // Tier filter
    if (tierFilter !== 'all') {
      filtered = filtered.filter(affiliate => affiliate.tier === tierFilter);
    }

    setFilteredAffiliates(filtered);
  };

  const handleApproveAffiliate = async (affiliateId) => {
    try {
      const token = Cookies.get('enakid_token');
      
      if (!token) {
        alert('Authentication required. Please log in as admin.');
        return;
      }
      
      const response = await axios.put(`${BACKEND_URL}/api/admin/affiliates/${affiliateId}/approve`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data) {
        alert('Affiliate approved successfully!');
        fetchAffiliates(); // Refresh the list
      }
    } catch (error) {
      console.error('Error approving affiliate:', error);
      alert(`Failed to approve affiliate: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleUpdateAffiliate = async (e) => {
    e.preventDefault();
    if (!selectedAffiliate) return;

    try {
      const token = Cookies.get('enakid_token');
      
      if (!token) {
        alert('Authentication required. Please log in as admin.');
        return;
      }
      
      const updateData = {};
      if (editForm.status) updateData.status = editForm.status;
      if (editForm.tier) updateData.tier = editForm.tier;
      if (editForm.commission_rate_override) {
        updateData.commission_rate_override = parseFloat(editForm.commission_rate_override);
      }
      if (editForm.notes) updateData.notes = editForm.notes;

      const response = await axios.put(`${BACKEND_URL}/api/admin/affiliates/${selectedAffiliate.id}`, updateData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data) {
        alert('Affiliate updated successfully!');
        setShowEditDialog(false);
        fetchAffiliates();
      }
    } catch (error) {
      console.error('Error updating affiliate:', error);
      alert(`Failed to update affiliate: ${error.response?.data?.detail || error.message}`);
    }
  };

  const openEditDialog = (affiliate) => {
    setSelectedAffiliate(affiliate);
    setEditForm({
      status: affiliate.status,
      tier: affiliate.tier,
      commission_rate_override: affiliate.commission_rate_override?.toString() || '',
      notes: affiliate.notes || ''
    });
    setShowEditDialog(true);
  };

  const openDetailsDialog = (affiliate) => {
    setSelectedAffiliate(affiliate);
    setShowDetailsDialog(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      suspended: 'bg-red-100 text-red-800 border-red-200',
      rejected: 'bg-gray-100 text-gray-800 border-gray-200',
      inactive: 'bg-gray-100 text-gray-600 border-gray-200'
    };
    return colors[status] || colors.pending;
  };

  const getTierColor = (tier) => {
    const colors = {
      bronze: 'bg-amber-100 text-amber-800 border-amber-200',
      silver: 'bg-gray-100 text-gray-800 border-gray-200',
      gold: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      platinum: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[tier] || colors.bronze;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Affiliate Management</h1>
          <p className="text-gray-500">Manage affiliate applications and performance</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Affiliates</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{affiliates.length}</div>
            <p className="text-xs text-muted-foreground">
              {affiliates.filter(a => a.status === 'approved').length} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Approvals</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {affiliates.filter(a => a.status === 'pending').length}
            </div>
            <p className="text-xs text-muted-foreground">
              Awaiting review
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(affiliates.reduce((sum, a) => sum + (a.lifetime_earnings || 0), 0))}
            </div>
            <p className="text-xs text-muted-foreground">
              All time payouts
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversions</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {affiliates.reduce((sum, a) => sum + (a.total_conversions || 0), 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Total referrals
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by name, email, or affiliate code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tierFilter} onValueChange={setTierFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Tiers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="bronze">Bronze</SelectItem>
                <SelectItem value="silver">Silver</SelectItem>
                <SelectItem value="gold">Gold</SelectItem>
                <SelectItem value="platinum">Platinum</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Affiliates Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Affiliates ({filteredAffiliates.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredAffiliates.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2">Affiliate</th>
                    <th className="text-left py-3 px-2">Status</th>
                    <th className="text-left py-3 px-2">Tier</th>
                    <th className="text-left py-3 px-2">Performance</th>
                    <th className="text-left py-3 px-2">Earnings</th>
                    <th className="text-left py-3 px-2">Joined</th>
                    <th className="text-left py-3 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAffiliates.map((affiliate) => (
                    <tr key={affiliate.id} className="border-b hover:bg-gray-50">
                      <td className="py-4 px-2">
                        <div>
                          <div className="font-medium">
                            {affiliate.user?.full_name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {affiliate.user?.email}
                          </div>
                          <div className="text-xs text-gray-400">
                            Code: {affiliate.affiliate_code}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-2">
                        <Badge className={getStatusColor(affiliate.status)}>
                          {affiliate.status.charAt(0).toUpperCase() + affiliate.status.slice(1)}
                        </Badge>
                      </td>
                      <td className="py-4 px-2">
                        <Badge className={getTierColor(affiliate.tier)}>
                          <Award className="h-3 w-3 mr-1" />
                          {affiliate.tier.charAt(0).toUpperCase() + affiliate.tier.slice(1)}
                        </Badge>
                      </td>
                      <td className="py-4 px-2">
                        <div className="text-sm">
                          <div>{affiliate.total_conversions || 0} conversions</div>
                          <div className="text-gray-500">{affiliate.total_clicks || 0} clicks</div>
                        </div>
                      </td>
                      <td className="py-4 px-2">
                        <div className="text-sm">
                          <div className="font-medium">
                            {formatCurrency(affiliate.lifetime_earnings)}
                          </div>
                          <div className="text-gray-500">
                            Balance: {formatCurrency(affiliate.current_balance)}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-2">
                        <div className="text-sm text-gray-600">
                          {formatDate(affiliate.created_at)}
                        </div>
                      </td>
                      <td className="py-4 px-2">
                        <div className="flex items-center space-x-2">
                          {affiliate.status === 'pending' && (
                            <Button
                              size="sm"
                              onClick={() => handleApproveAffiliate(affiliate.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approve
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDetailsDialog(affiliate)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(affiliate)}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No affiliates found</h3>
              <p className="text-gray-500">No affiliates match your current filters.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Affiliate Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Affiliate Details</DialogTitle>
            <DialogDescription>
              Complete information for {selectedAffiliate?.user?.full_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedAffiliate && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-600">Email</Label>
                  <p className="text-sm">{selectedAffiliate.user?.email}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Affiliate Code</Label>
                  <p className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                    {selectedAffiliate.affiliate_code}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Business Name</Label>
                  <p className="text-sm">{selectedAffiliate.business_name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Website</Label>
                  <p className="text-sm">
                    {selectedAffiliate.website ? (
                      <a href={selectedAffiliate.website} target="_blank" rel="noopener noreferrer" 
                         className="text-blue-600 hover:underline flex items-center">
                        <Globe className="h-3 w-3 mr-1" />
                        {selectedAffiliate.website}
                      </a>
                    ) : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Performance Metrics */}
              <div>
                <Label className="text-sm font-medium text-gray-600 mb-3 block">Performance</Label>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-lg font-bold text-blue-600">
                      {selectedAffiliate.total_clicks || 0}
                    </div>
                    <div className="text-xs text-blue-600">Clicks</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-lg font-bold text-green-600">
                      {selectedAffiliate.total_conversions || 0}
                    </div>
                    <div className="text-xs text-green-600">Conversions</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-lg font-bold text-purple-600">
                      {formatCurrency(selectedAffiliate.lifetime_earnings)}
                    </div>
                    <div className="text-xs text-purple-600">Total Earned</div>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <div className="text-lg font-bold text-orange-600">
                      {formatCurrency(selectedAffiliate.current_balance)}
                    </div>
                    <div className="text-xs text-orange-600">Balance</div>
                  </div>
                </div>
              </div>

              {/* Marketing Info */}
              {(selectedAffiliate.target_audience || selectedAffiliate.marketing_experience) && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-600">Marketing Information</Label>
                  {selectedAffiliate.target_audience && (
                    <div>
                      <Label className="text-xs text-gray-500">Target Audience</Label>
                      <p className="text-sm bg-gray-50 p-2 rounded">{selectedAffiliate.target_audience}</p>
                    </div>
                  )}
                  {selectedAffiliate.marketing_experience && (
                    <div>
                      <Label className="text-xs text-gray-500">Marketing Experience</Label>
                      <p className="text-sm bg-gray-50 p-2 rounded">{selectedAffiliate.marketing_experience}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Admin Notes */}
              {selectedAffiliate.notes && (
                <div>
                  <Label className="text-sm font-medium text-gray-600">Admin Notes</Label>
                  <p className="text-sm bg-yellow-50 p-3 rounded border border-yellow-200">
                    {selectedAffiliate.notes}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Affiliate Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Affiliate</DialogTitle>
            <DialogDescription>
              Update affiliate status, tier, and settings
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUpdateAffiliate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-status">Status</Label>
                <Select value={editForm.status} onValueChange={(value) => setEditForm(prev => ({...prev, status: value}))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="edit-tier">Tier</Label>
                <Select value={editForm.tier} onValueChange={(value) => setEditForm(prev => ({...prev, tier: value}))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bronze">Bronze (20%)</SelectItem>
                    <SelectItem value="silver">Silver (25%)</SelectItem>
                    <SelectItem value="gold">Gold (30%)</SelectItem>
                    <SelectItem value="platinum">Platinum (35%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="commission-override">Custom Commission Rate (%)</Label>
              <Input
                id="commission-override"
                type="number"
                min="0"
                max="50"
                step="0.1"
                value={editForm.commission_rate_override}
                onChange={(e) => setEditForm(prev => ({...prev, commission_rate_override: e.target.value}))}
                placeholder="Leave empty to use tier default"
              />
            </div>

            <div>
              <Label htmlFor="admin-notes">Admin Notes</Label>
              <Textarea
                id="admin-notes"
                value={editForm.notes}
                onChange={(e) => setEditForm(prev => ({...prev, notes: e.target.value}))}
                placeholder="Internal notes about this affiliate..."
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Update Affiliate
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AffiliateManagement;